import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas-pro";
import QuotationPrintTable from "./QuotationPrintTable";

const QuotationPrintModal = ({ record, onClose }) => {
  const captureRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [generating, setGenerating] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!record) return;

    let cancelled = false;
    setGenerating(true);
    setError("");
    setImageUrl(null);

    // Small delay so the off-screen table has fully laid out (fonts,
    // table widths) before html2canvas measures and captures it.
    const timer = setTimeout(async () => {
      try {
        if (!captureRef.current) return;

        const canvas = await html2canvas(captureRef.current, {
          backgroundColor: "#ffffff",
          scale: 2, // sharper output for text-heavy tables
          useCORS: true,
        });

        if (cancelled) return;

        const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
        setImageUrl(dataUrl);
      } catch (err) {
        if (!cancelled) {
          setError("Failed to generate the preview. Please try again.");
          console.error(err);
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    }, 80);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [record]);

  if (!record) return null;

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `Quotation-${record.quotationNo || "draft"}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      {/* Off-screen render target — html2canvas needs the node actually
          laid out in the DOM, so it's positioned far outside the visible
          viewport rather than display:none (which would give a 0x0
          capture). */}
      <div style={{ position: "fixed", left: "-99999px", top: 0 }}>
        <QuotationPrintTable record={record} ref={captureRef} />
      </div>

      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Print Preview
            </h2>
            <p className="mt-0.5 text-xs text-gray-400">
              Quotation No {record.quotationNo}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 px-5 py-6">
          {generating ? (
            <div className="flex h-64 items-center justify-center text-sm text-gray-400">
              Generating preview...
            </div>
          ) : error ? (
            <div className="flex h-64 items-center justify-center text-sm text-red-500">
              {error}
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={`Quotation ${record.quotationNo}`}
              className="mx-auto max-w-full rounded-lg border border-gray-200 shadow-sm"
            />
          )}
        </div>

        <div className="flex gap-2 border-t border-gray-100 px-5 py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            disabled={!imageUrl || generating}
            className="flex-1 rounded-lg bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Download as JPG
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotationPrintModal;